// apps/admin/src/components/composer/preview-pane.tsx
//
// P5 UI Polish W2 T7 — Live preview pane (spec §3.3).
// P5 UI Polish W5 T5 — Updated to use PreviewRenderer instead of gradient
//   placeholders. Zones with a content binding in `contentMap` show their
//   actual content type. Zones absent from `contentMap` fall back to the W2
//   gradient placeholder + zone-name label.
// P5 UI Polish W6 T4 — Added `bindings` prop; PreviewPane now pre-fetches
//   signed URLs for image/video assets via getResourceSignedUrl (T3 wrapper)
//   and merges them into the contentMap passed to PreviewRenderer. On fetch
//   failure: console.warn + falls back to type-badge (no throw; never blocks
//   the render).
//
// NOTE: Zone (from @displaycms/contracts) does not carry `name_i18n`
// today — every real zone will therefore show its id as the label.
// The prop type extends Zone with an optional `name_i18n` so that
// future callers (or test fixtures) can supply human-readable names
// without a schema change.
//
// Wired (SP3d): layout-editor.tsx now resolves each zone's first program item
// → a ZoneBinding (kind + resource_version_id/source_url) and passes them as
// `bindings`, so the editor preview shows real content. `bindings` stays
// optional, so other callers (and tests) can still render placeholder-only.

import { useEffect, useRef, useState } from 'react';
import type { Zone } from '@displaycms/contracts';
import type { ResourceKind } from '@displaycms/contracts';
import { getResourceSignedUrl } from '@/api/resources';
import { PreviewRenderer, type ZoneContent } from './preview-renderer';

export interface ZoneWithOptionalName extends Zone {
  name_i18n?: { fr?: string; en?: string };
}

/**
 * A pre-resolved content binding for a single zone, ready for the renderer.
 * The caller is responsible for resolving kind/source_url from the
 * ResourceVersion (ProgramItemSummary only carries resource_version_id).
 *
 * image / video entries: set `resource_version_id` so the pane can fetch
 * a signed URL. The pane will add `signed_url` to the ZoneContent entry.
 *
 * rss_feed / web_page entries: set `source_url` (no signed URL needed).
 * audio entries: only `label` is used (badge-only; no URL to fetch).
 */
export interface ZoneBinding {
  zone_id: string;
  kind: ResourceKind;
  /** ResourceVersion id — required for image/video so signed URL can be fetched. */
  resource_version_id?: string | null;
  /** External URL — used directly for rss_feed/web_page. */
  source_url?: string | null;
  /** Human-readable name shown in the badge fallback. */
  label?: string;
}

export interface PreviewPaneProps {
  zones: ReadonlyArray<ZoneWithOptionalName>;
  /** Optional: pre-resolved content bindings keyed by zone id.
   *  Zones absent from this map render the W2 gradient placeholder. */
  contentMap?: Record<string, ZoneContent>;
  /**
   * W6 T4: Optional pre-resolved bindings. When provided, PreviewPane
   * fetches signed URLs for image/video entries and builds the contentMap
   * automatically. Takes precedence over `contentMap` when both are present.
   */
  bindings?: ZoneBinding[];
}

export function PreviewPane({ zones, contentMap: contentMapProp, bindings }: PreviewPaneProps): JSX.Element {
  const [fetchedContentMap, setFetchedContentMap] = useState<Record<string, ZoneContent> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!bindings || bindings.length === 0) {
      setFetchedContentMap(null);
      return;
    }

    cancelledRef.current = false;

    async function load(): Promise<void> {
      if (!bindings) return;
      const controller = new AbortController();
      const entries: Array<[string, ZoneContent]> = [];

      for (const binding of bindings) {
        if (cancelledRef.current) break;

        if ((binding.kind === 'image' || binding.kind === 'video') && binding.resource_version_id) {
          try {
            const { url } = await getResourceSignedUrl(binding.resource_version_id, {
              signal: controller.signal,
            });
            const entry: ZoneContent = { kind: binding.kind, signed_url: url };
            if (binding.label !== undefined) entry.label = binding.label;
            entries.push([binding.zone_id, entry]);
          } catch (err) {
            console.warn(
              '[PreviewPane] Signed URL fetch failed for resource_version_id',
              binding.resource_version_id,
              err,
            );
            // Fall back: no signed_url → renderer uses type-badge
            const entry: ZoneContent = { kind: binding.kind };
            if (binding.label !== undefined) entry.label = binding.label;
            entries.push([binding.zone_id, entry]);
          }
        } else {
          // rss_feed, web_page, audio — no signed URL needed
          const entry: ZoneContent = {
            kind: binding.kind,
            source_url: binding.source_url ?? null,
          };
          if (binding.label !== undefined) entry.label = binding.label;
          entries.push([binding.zone_id, entry]);
        }
      }

      if (!cancelledRef.current) {
        setFetchedContentMap(Object.fromEntries(entries));
      }
    }

    void load();

    return () => {
      cancelledRef.current = true;
    };
  }, [bindings]);

  // bindings (with sign-URL fetch) takes precedence over the static contentMap prop
  const effectiveContentMap = fetchedContentMap ?? contentMapProp;

  return (
    <div
      data-testid="preview-pane"
      className="relative h-full w-full bg-black overflow-hidden"
    >
      <PreviewRenderer
        zones={zones}
        {...(effectiveContentMap !== undefined ? { contentMap: effectiveContentMap } : {})}
        canvasWidth={1920}
        canvasHeight={1080}
      />
    </div>
  );
}
